// Deleting a whole post, said in the two places it is offered: the posts list
// and the post page.
//
// The sentence in here is the most consequential text in the product — the
// difference between "this cancels the post" and "this stays live and you will
// have to remove it yourself" — so it is written once. The modal's behaviour
// (Escape, backdrop, locking while the request is in flight) comes from
// `ConfirmDialog`, which every destructive confirmation shares.
//
// It does NOT unpublish, and the text says where the post stays rather than
// claiming the platforms forbid taking it down — both networks do document
// deletion, and this product deliberately does not use it (migration
// 20260908000300).

import { deletePost } from "../lib/api";
import { logError } from "../lib/logger";
import { ConfirmDialog } from "./ConfirmDialog";

export function DeletePostDialog({
  postId, label, published, onCancel, onDeleted,
}: {
  postId: string;
  /** What to call the post being deleted, so a list of six is unambiguous. */
  label: string;
  /** How many networks already have it. The whole shape of the warning. */
  published: number;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  return (
    <ConfirmDialog
      title="Delete this post?"
      lede={label}
      confirmLabel={published > 0 ? "Delete the record" : "Delete this post"}
      onCancel={onCancel}
      onConfirm={async () => {
        try {
          await deletePost(postId);
          onDeleted();
        } catch (e: any) {
          void logError("post_delete", e, { step: "delete_post", postId });
          throw e;
        }
      }}
    >
      <p>
        {published > 0
          ? <>
              This post is live on {published === 1 ? "one network" : `${published} networks`}.
              Deleting it here <strong>does not take it down</strong> — it stays exactly where it
              was published, and you would need to remove it in Instagram or Facebook yourself.
              What goes is the record here: the caption, the timings and the per-network result.
            </>
          : <>
              This post has not gone out. Deleting it cancels it — nothing will be published —
              and removes it from your calendar. The photos stay in your media library.
            </>}
      </p>
    </ConfirmDialog>
  );
}
